import React, { useState, useEffect, useCallback } from 'react';
import LoadErrorBox from './ui/LoadErrorBox';
import { daysUntil, localDayKey } from '../utils/localDate';
import { useNavigate } from 'react-router-dom';
import { BudgetCategory } from '../types/ui/Budget';
import Icon from './Icon';
import '../styles/components/Dashboard.css';
import { calculateBudgetSummary, refreshBudgetFromApi } from '../utils/budgetStorage';
import { loadDashboardData } from '../utils/dashboardData';
import { loadTasks, Task } from '../services/TasksService';
import { loadGuests, getGuestStats, refreshGuestsFromApi, fetchServerGuestStats, loadServerGuestStats, type ServerGuestStats } from '../services/GuestService';
import { GUESTS_ENABLED, EVENTS_ENABLED } from '../constants/featureFlags';
import { coupleLine } from '../utils/coupleNames';
import { useStoredEventsState } from '../hooks/useStoredEvents';
import { getCachedVenues } from '../services/VenueApiService';
import { useSettings } from '../contexts/SettingsContext';
import { loadActionLog, describeEntity, ActionLogEntry } from '../services/ActionLogService';
import { loadSettingsFromDb } from '../services/SettingsService';
import {
  PlanningPhaseId,
  PhaseData,
  DATA_MILESTONES,
  PHASE_TASK_WEIGHT,
  PHASE_MILESTONE_WEIGHT,
} from '../constants/planningPhases';

const ACTIVITY_ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
};

// The badge already shows the verb (Created/Updated/Deleted) — strip a
// leading duplicate of it from the summary so rows don't read "CREATED Created quotes".
function stripLeadingVerb(summary: string, verb: string): string {
  const re = new RegExp(`^${verb}\\s+`, 'i');
  return summary.replace(re, '').trimStart();
}

function formatActivityTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

// ─── Planning phase logic ──────────────────────────────────────────────────────

interface Phase {
  id: string;
  label: string;
  minMonths: number;
  maxMonths: number | null;
  color: string;
  description: string;
}

const PHASES: Phase[] = [
  { id: 'foundation',  label: 'Foundation',   minMonths: 12, maxMonths: null, color: '#8b5a3c', description: 'Events defined, guest list started, budget framework' },
  { id: 'design',      label: 'Design',       minMonths: 9,  maxMonths: 12,   color: '#b8734a', description: 'Aesthetic decisions, asoebi, venue shortlisting' },
  { id: 'lock-in',     label: 'Lock-In',      minMonths: 6,  maxMonths: 9,    color: '#c4983a', description: 'Vendor contracts signed, deposits paid, RSVPs open' },
  { id: 'make-order',  label: 'Make & Order', minMonths: 3,  maxMonths: 6,    color: '#6b8c42', description: 'Outfits in production, orders placed, seating drafted' },
  { id: 'finalise',    label: 'Finalise',     minMonths: 1,  maxMonths: 3,    color: '#2d8a7a', description: 'Logistics, stationery, final payments, rehearsal' },
  { id: 'event-week',  label: 'Event Week',   minMonths: 0,  maxMonths: 1,    color: '#9b4dca', description: 'Day-of timeline, team assignments, pack bags' },
];

function getPhase(projectDate: Date): Phase {
  const today = new Date();
  const diffMs = projectDate.getTime() - today.getTime();
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44);
  if (diffMonths <= 0) return PHASES[4];
  for (const phase of PHASES) {
    if (diffMonths >= phase.minMonths && (phase.maxMonths === null || diffMonths < phase.maxMonths)) {
      return phase;
    }
  }
  return PHASES[0];
}

function getDaysTo(projectDate: Date): number {
  // Local calendar days, so the countdown flips at the viewer's midnight.
  return Math.max(0, daysUntil(projectDate) ?? 0);
}

function getHybridPhaseProgress(
  currentPhase: Phase,
  phaseData: PhaseData,
): { pct: number; tasksDone: number; tasksTotal: number; milestonesMet: number; milestonesTotal: number } {
  const phaseTasks = phaseData.tasks.filter(t => t.phase === currentPhase.id && t.taskType !== 'run_of_show');
  const tasksDone = phaseTasks.filter(t => t.status === 'Done').length;
  const tasksTotal = phaseTasks.length;
  const taskProgress = tasksTotal > 0 ? tasksDone / tasksTotal : 0;

  const milestones = DATA_MILESTONES[currentPhase.id as PlanningPhaseId] ?? [];
  const milestonesMet = milestones.filter(m => m.check(phaseData)).length;
  const milestonesTotal = milestones.length;
  const milestoneProgress = milestonesTotal > 0 ? milestonesMet / milestonesTotal : 0;

  const pct = milestonesTotal > 0
    ? Math.round((taskProgress * PHASE_TASK_WEIGHT + milestoneProgress * PHASE_MILESTONE_WEIGHT) * 100)
    : Math.round(taskProgress * 100);
  return { pct, tasksDone, tasksTotal, milestonesMet, milestonesTotal };
}

// ─── Next actions ──────────────────────────────────────────────────────────────

/** Tasks whose completion is auto-derived from app data rather than a manual click */
const DERIVED_TASK_NAMES: string[] = ['Floor Plan Design'];

/**
 * Returns true if the "Floor Plan Design" task is effectively done —
 * i.e. at least one venue exists with at least one floor plan that has rooms.
 */
function isFloorPlanDesignDone(): boolean {
  try {
    const venues = getCachedVenues();
    return venues.some(v => v.floorPlans?.some(fp => (fp.rooms?.length ?? 0) > 0));
  } catch {
    return false;
  }
}

function isDerivedTaskDone(taskName: string): boolean {
  if (taskName === 'Floor Plan Design') return isFloorPlanDesignDone();
  return false;
}

function sortNextActions(tasks: Task[]): Task[] {
  const today = new Date();
  return [...tasks].sort((a, b) => {
    const aOverdue = a.dueDateISO && new Date(a.dueDateISO) < today ? 1 : 0;
    const bOverdue = b.dueDateISO && new Date(b.dueDateISO) < today ? 1 : 0;
    if (bOverdue !== aOverdue) return bOverdue - aOverdue;
    if (a.priority !== b.priority) return a.priority < b.priority ? -1 : 1;
    return 0;
  });
}

function getNextActionCandidates(tasks: Task[]): Task[] {
  return tasks.filter(t =>
    t.status !== 'Done' &&
    !t.archived &&
    (t.priority === 'P0' || t.priority === 'P1') &&
    // exclude tasks whose completion is auto-derived and already done
    !(DERIVED_TASK_NAMES.includes(t.task) && isDerivedTaskDone(t.task))
  );
}

const MAX_ACTIONS_PREVIEW = 2;

interface PhaseActionGroup {
  phase: Phase | null;
  total: number;
  preview: Task[];
}

/** Next actions grouped by planning phase, in phase order — `total` is the
 * full open P0/P1 count for that phase, `preview` the top few by
 * urgency/priority for compact display. Tasks with no phase (or a phase id
 * that isn't one of the six shown in the stepper) fall into a trailing
 * "Unphased" group so nothing silently disappears. */
function getNextActionsByPhase(tasks: Task[]): PhaseActionGroup[] {
  const candidates = getNextActionCandidates(tasks);
  const groups: PhaseActionGroup[] = [];

  for (const phase of PHASES) {
    const inPhase = sortNextActions(candidates.filter(t => t.phase === phase.id));
    if (inPhase.length > 0) groups.push({ phase, total: inPhase.length, preview: inPhase.slice(0, MAX_ACTIONS_PREVIEW) });
  }

  const phaseIds = new Set(PHASES.map(p => p.id));
  const unphased = sortNextActions(candidates.filter(t => !t.phase || !phaseIds.has(t.phase)));
  if (unphased.length > 0) groups.push({ phase: null, total: unphased.length, preview: unphased.slice(0, MAX_ACTIONS_PREVIEW) });

  return groups;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { settings, onboardingData, formatCurrency } = useSettings();
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [budgetAvailable, setBudgetAvailable] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [guests, setGuests] = useState(loadGuests());
  // The API's own project-wide guest totals; null until fetched, then guestStats counts the loaded rows instead.
  const [serverGuestStats, setServerGuestStats] = useState<ServerGuestStats | null>(() => loadServerGuestStats());
  // Same loader as the Calendar: reads the cache, fetches when nothing has, and re-reads when the load lands.
  const { events, loaded: eventsLoadedOnce } = useStoredEventsState();
  // 'ready' only after the guest list really loaded: an empty or failed load must not read as "no honorees".
  const [guestLoad, setGuestLoad] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [recentActivity, setRecentActivity] = useState<ActionLogEntry[]>([]);
  // settings.project.guestCapacity (SettingsContext) has no such field — the
  // real, DB-backed value lives in DbSettings (SettingsService), synced via
  // /planning/:id/settings and never auto-loaded at boot.
  const [guestCapacity, setGuestCapacity] = useState<number | undefined>(undefined);

  // Compute the project date: pick the earliest future (or most recent past) event with a valid date.
  // Dates may be ISO "2026-12-05" or natural "December 5, 2026".
  const projectDate = (() => {
    const parseEventDate = (raw: string): string | null => {
      if (!raw) return null;
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
      const d = new Date(raw);
      if (!isNaN(d.getTime())) return localDayKey(d);
      return null;
    };
    const dated = events
      .map(e => ({ e, iso: parseEventDate(e.date ?? '') }))
      .filter((x): x is { e: typeof x.e; iso: string } => x.iso !== null);
    // No dated event: no countdown, rather than counting down to today.
    if (dated.length === 0) return null;
    dated.sort((a, b) => a.iso.localeCompare(b.iso));
    const today = localDayKey();
    const future = dated.find(x => x.iso >= today);
    const chosen = future ?? dated[dated.length - 1];
    return new Date(`${chosen.iso}T00:00:00`);
  })();

  const refreshTasks = useCallback(() => {
    setTasks(loadTasks().filter(t => !t.archived));
  }, []);

  const fetchData = useCallback(async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // Guests are no longer fetched: their API route is disabled (moved
        // to Kalligo), so only the local cache is read. Events come from useStoredEvents.
        const { tasks: apiTasks, budgetCategories: categories, budgetAvailable: hasBudget } = await loadDashboardData();
        setBudgetCategories(categories.filter(c => !c.archived));
        setBudgetAvailable(hasBudget);
        setTasks(apiTasks.filter(t => !t.archived));
        setGuests(loadGuests());
        if (GUESTS_ENABLED) {
          // The real guest list and totals; a failure leaves the cached figures, it never blocks the dashboard.
          refreshGuestsFromApi()
            .then(() => { setGuests(loadGuests()); setGuestLoad('ready'); })
            .catch(() => { setGuestLoad('failed'); /* keep the cache */ });
          fetchServerGuestStats().then(stats => { if (stats) setServerGuestStats(stats); });
        }
        loadActionLog(5).then(setRecentActivity).catch(() => setRecentActivity([]));
        loadSettingsFromDb().then(s => setGuestCapacity(s.project.guestCapacity || undefined)).catch(() => {});
      } catch (error) {
        console.error('Error fetching dashboard data', error);
        // Kept on the page with a Retry; zeros here would read as real numbers.
        setLoadError(error ?? new Error('load failed'));
      } finally {
        setLoading(false);
      }
  }, []);

  useEffect(() => {
    refreshTasks();
    fetchData();
  }, [refreshTasks, fetchData]);

  // Reload when another tab changes tasks, budget, or any synced key
  useEffect(() => {
    const reload = () => {
      refreshTasks();
      void refreshBudgetFromApi().then(cats => setBudgetCategories(cats.filter(c => !c.archived))).catch(() => {});
    };
    window.addEventListener('anojeje:storage-sync', reload);
    return () => window.removeEventListener('anojeje:storage-sync', reload);
  }, [refreshTasks]);

  // The empty state is only honest once the events are known: not while loading, not after a failed load.
  const eventsKnown = eventsLoadedOnce || !EVENTS_ENABLED;
  const phase = projectDate ? getPhase(projectDate) : null;
  const daysTo = projectDate ? getDaysTo(projectDate) : null;

  // Build PhaseData for hybrid progress
  const phaseData: PhaseData = {
    vendors: budgetCategories.flatMap(cat => cat.vendors.map(v => ({ category: v.service ?? '' }))),
    guests,
    budgetCategories,
    tasks: tasks.map(t => ({ phase: t.phase, status: t.status, taskType: t.taskType })),
  };
  const hybridProgress = phase ? getHybridPhaseProgress(phase, phaseData) : null;
  const phaseProgress = hybridProgress?.pct ?? 0;
  const nextActionGroups = getNextActionsByPhase(tasks);
  const nextActionsByPhaseId = new Map(
    nextActionGroups.filter((g): g is PhaseActionGroup & { phase: Phase } => g.phase !== null).map(g => [g.phase.id, g])
  );

  const topBudgetCategories = [...budgetCategories]
    .sort((a, b) => b.planned_amount - a.planned_amount)
    .slice(0, 4);

  // Guest stats
  const guestStats = getGuestStats(guests, guestCapacity, serverGuestStats);
  // Relationship-group breakdown derived from the guests' own tags — no
  // hardcoded family names, works for any couple's tag set.
  const relationshipTags = new Map<string, { tagId: string; label: string; color: string; count: number }>();
  guests.forEach(guest => {
    guest.tags
      .filter(t => t.category === 'relationship')
      .forEach(t => {
        const existing = relationshipTags.get(t.id);
        if (existing) {
          existing.count += 1;
        } else {
          relationshipTags.set(t.id, { tagId: t.id, label: t.label, color: t.color, count: 1 });
        }
      });
  });
  const groupCounts = Array.from(relationshipTags.values()).sort((a, b) => b.count - a.count);
  const maxGroupCount = Math.max(...groupCounts.map(g => g.count), 1);

  // Venue + theme from events
  const activeEvents = events.filter(e => !e.archived);
  const receptionEvent = activeEvents.find(e =>
    e.category?.toLowerCase().includes('reception') || e.title?.toLowerCase().includes('reception')
  );
  const churchEvent = activeEvents.find(e =>
    e.category?.toLowerCase().includes('church') || e.title?.toLowerCase().includes('church')
  );
  const receptionVenue = receptionEvent?.venue || receptionEvent?.location || '—';
  const churchVenue = churchEvent?.venue || churchEvent?.location || '—';
  // Collect unique, non-empty themes from all active events (in event order)
  const projectTheme = activeEvents
    .map(e => e.theme?.trim())
    .filter((t): t is string => !!t)
    .join(' · ') || '—';

  // The couple is the project's honoree guests (Settings no longer hold names); a prompt shows when there are none.
  const coupleNames = coupleLine(guests);
  const hashtag = settings.project.hashtag || onboardingData.hashtag || '';
  const instagram = onboardingData.instagram || '';

  const getProgress = (cat: BudgetCategory) =>
    cat.planned_amount === 0 ? 0 : Math.min(100, Math.round((cat.actual_amount / cat.planned_amount) * 100));

  const budgetSummary = calculateBudgetSummary(budgetCategories);
  const deliverableTasks = tasks.filter(t => t.taskType !== 'run_of_show');
  const doneTasks = deliverableTasks.filter(t => t.status === 'Done').length;
  const totalTasks = deliverableTasks.length;
  const inProgressTasks = deliverableTasks.filter(t => t.status === 'In Progress').length;
  // blockedOn is a facet independent of status now — a blocked task also
  // counts toward its real status bucket above, so this bar is no longer
  // mutually exclusive with the other three (it never summed to totalTasks
  // as a stacked chart anyway — see the bar rendering below).
  const blockedTasks = deliverableTasks.filter(t => !!t.blockedOn).length;
  const notStartedTasks = deliverableTasks.filter(t => t.status === 'Not Started').length;
  const taskCompletionPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

  if (loading) {
    return (
      <div className="wt-container fade-in">
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading dashboard data...</p>
          </div>
      </div>
    );
  }

  return (
    <div className="wt-container db-page fade-in">
        {loadError ? (
          <LoadErrorBox what="your tasks and budget" error={loadError} onRetry={fetchData} />
        ) : null}
        {/* ── Phase Indicator ── */}
        <div className="db-phase-card">
          <div className="db-phase-top">
            <div>
              <div className="db-phase-label">
                Expected Phase
                <span
                  className="db-phase-label-info"
                  title="Based on today's date vs. your project date — a generic timeline expectation, not a measure of what you've actually completed. Check the phases below for real progress."
                >
                  <Icon name="helpCircle" size={12} />
                </span>
              </div>
              {phase ? (
                <>
                  <div className="db-phase-name">
                    <span className="db-phase-name-dot" style={{ /* ds-dynamic: phase color from data */ background: phase.color }} />
                    {phase.label}
                  </div>
                  <div className="db-phase-desc">{phase.description}</div>
                </>
              ) : eventsKnown ? (
                <>
                  <div className="db-phase-name">No event dates yet</div>
                  <div className="db-phase-desc">
                    Add a date to an event and the countdown and planning phase start here.
                    {EVENTS_ENABLED && (
                      <>
                        {' '}
                        <button type="button" className="db-card-view-all" onClick={() => navigate('/events')}>Go to Events</button>
                      </>
                    )}
                  </div>
                </>
              ) : null}
            </div>
            {daysTo !== null && (
              <div className="db-phase-days">
                <div className="db-phase-days-num">{daysTo}</div>
                <div className="db-phase-days-label">days to go</div>
              </div>
            )}
          </div>
          <div className="db-phase-body">
          <div className="db-phase-steps">
            {(() => {
              const currentIdx = phase ? PHASES.findIndex(ph => ph.id === phase.id) : -1;
              return PHASES.map((p, idx) => {
                const status = idx < currentIdx ? 'done' : idx === currentIdx ? 'active' : 'upcoming';
                const actionGroup = nextActionsByPhaseId.get(p.id);
                // "done" here only means the timeline has moved past this
                // phase — it says nothing about whether the phase's own
                // work actually finished. Surface that gap explicitly
                // instead of letting a dimmed dot imply "handled".
                const isBehind = status === 'done' && !!actionGroup && actionGroup.total > 0;
                return (
                  <div
                    key={p.id}
                    className={`db-phase-step db-phase-step--${status}${isBehind ? ' db-phase-step--behind' : ''}`}
                    title={isBehind ? `${p.label} — timeline has moved on, but ${actionGroup!.total} open P0/P1 action${actionGroup!.total === 1 ? '' : 's'} still remain` : p.label}
                  >
                    <div className="db-phase-dot-wrap">
                      <div className="db-phase-dot" style={{ /* ds-dynamic: phase color from data, conditional on status */ background: status === 'upcoming' ? '#e5e7eb' : p.color }} />
                      {isBehind && <span className="db-phase-dot-behind-flag">!</span>}
                    </div>
                    <div className="db-phase-step-label">{p.label}</div>
                    {actionGroup && (
                      <div
                        className="db-phase-step-actions"
                        title={`${actionGroup.total} open P0/P1 action${actionGroup.total === 1 ? '' : 's'} in ${p.label}`}
                      >
                        <span className="db-phase-step-count">{actionGroup.total} task{actionGroup.total === 1 ? '' : 's'}</span>
                        {actionGroup.preview.map(t => (
                          <button
                            key={t.id}
                            className="db-phase-step-action-title"
                            onClick={() => navigate(`/activities?task=${t.id}`)}
                          >
                            {t.task}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
          <div className="db-phase-bar-wrap">
            <div className="db-phase-bar db-phase-bar--segmented">
              {(() => {
                const currentIdx = phase ? PHASES.findIndex(p => p.id === phase.id) : -1;
                return PHASES.map((p, idx) => {
                  const segTasks = phaseData.tasks.filter(t => t.phase === p.id && t.taskType !== 'run_of_show');
                  const segDone = segTasks.filter(t => t.status === 'Done').length;
                  const fillPct = segTasks.length > 0 ? Math.round(segDone / segTasks.length * 100) : 0;
                  const isActive = idx === currentIdx;
                  const segClass = idx < currentIdx ? 'past' : isActive ? 'active' : 'upcoming';
                  return (
                    <div key={p.id} className={`db-phase-seg db-phase-seg--${segClass}`} title={`${p.label} — ${fillPct}% done (${segDone}/${segTasks.length})`}>
                      <div
                        className="db-phase-seg-fill"
                        style={{ /* ds-dynamic: fill percent + phase color computed per segment */ width: `${fillPct}%`, background: p.color }}
                      />
                    </div>
                  );
                });
              })()}
            </div>
            {phase && hybridProgress && (
              <span
                className="db-phase-bar-pct"
                title={`${hybridProgress.tasksDone} tasks done · ${hybridProgress.milestonesMet}/${hybridProgress.milestonesTotal} milestones`}
              >
                {phaseProgress}% through {phase.label}
                {hybridProgress.tasksTotal > 0 && (
                  <span className="db-phase-bar-detail">
                    &nbsp;({hybridProgress.tasksDone} tasks done{hybridProgress.milestonesTotal > 0 ? `, ${hybridProgress.milestonesMet}/${hybridProgress.milestonesTotal} milestones` : ''})
                  </span>
                )}
              </span>
            )}
          </div>
          </div>
        </div>

        {/* ── Project at a Glance ── */}
        <div className="db-glance-card">
          <div className="db-glance-left">
            <div className="db-glance-couple">
              {coupleNames ? (
                <span className="db-glance-names">{coupleNames}</span>
              ) : GUESTS_ENABLED && guestLoad === 'ready' ? (
                <button type="button" className="db-card-view-all" onClick={() => navigate('/guests')}>
                  Mark the couple as honorees in Guests
                </button>
              ) : null}
              {hashtag && <span className="db-glance-hashtag">{hashtag}</span>}
              {instagram && (
                <span className="db-glance-row db-glance-row--inline">
                  <Icon name="users" size={14} />
                  <span className="db-glance-label">Instagram</span>
                  <span className="db-glance-value">{instagram}</span>
                </span>
              )}
              <span className="db-glance-row db-glance-row--inline">
                <Icon name="users" size={14} />
                <span className="db-glance-label">Total Guests</span>
                <span className="db-glance-value">{guestStats.totalGuests} invited</span>
              </span>
            </div>
            {/* Events mini-list */}
            {activeEvents.length > 0 && (
              <div className="db-glance-events">
                <div className="db-glance-section-label">Events</div>
                {activeEvents.map(ev => (
                  <div key={ev.id} className="db-glance-event-row">
                    <div className="db-glance-event-dot" style={{ /* ds-dynamic: event color from data */ background: ev.eventColor || '#8b5a3c' }} />
                    <span className="db-glance-event-title">{ev.title}</span>
                    <span className="db-glance-event-date">{ev.date}{ev.time ? ` · ${ev.time}` : ''}</span>
                    <span className="db-glance-event-venue">{ev.venue || ev.location || ''}</span>
                    {ev.dresscode && <span className="db-glance-event-dresscode">{ev.dresscode}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="db-glance-right">
            {projectTheme && projectTheme !== '—' && (
              <>
                <div className="db-glance-theme-label db-glance-theme-label-mt">Theme</div>
                <div className="db-glance-theme-text">{projectTheme}</div>
              </>
            )}
          </div>
        </div>

        {/* ── Task Completion + Guest Group Breakdown + Recent Activity ── */}
        <div className="db-widgets-row">
          <div className="db-task-completion">
            <div className="progress-header">
              <h2>Task Completion</h2>
              <button type="button" className="db-card-view-all" onClick={() => navigate('/activities')}>View all →</button>
            </div>
            <div className="db-task-completion-headline">
              <span className="db-task-completion-count">{doneTasks} of {totalTasks}</span>
              <span className="db-task-completion-pct">done ({taskCompletionPct}%)</span>
            </div>
            <div className="db-task-completion-bar">
              <div className="db-task-completion-bar-fill" style={{ /* ds-dynamic: fill percent computed from task counts */ width: `${taskCompletionPct}%` }} />
            </div>
            <div className="db-task-completion-chart">
              {[
                { label: 'Done', value: doneTasks, tone: 'success' },
                { label: 'In Progress', value: inProgressTasks, tone: 'info' },
                { label: 'Not Started', value: notStartedTasks, tone: 'neutral' },
                { label: 'Blocked', value: blockedTasks, tone: 'danger' },
              ].map(({ label, value, tone }) => {
                const max = Math.max(doneTasks, inProgressTasks, notStartedTasks, blockedTasks, 1);
                return (
                  <div key={label} className="db-task-chart-col">
                    <strong className="db-task-chart-value">{value}</strong>
                    <div className="db-task-chart-track">
                      <div
                        className={`db-task-chart-bar db-task-chart-bar--${tone}`}
                        style={{ /* ds-dynamic: bar height computed from data */ height: `${Math.max((value / max) * 100, value > 0 ? 6 : 0)}%` }}
                      />
                    </div>
                    <span className="db-task-chart-label">{label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="db-guest-groups">
            <div className="progress-header">
              <h2>Guests by Family Group</h2>
            </div>
            {guestStats.capacityLimit ? (
              <>
                <div className="db-task-completion-headline">
                  <span className="db-task-completion-count">{guestStats.totalGuests + guestStats.totalPlusOnes} of {guestStats.capacityLimit}</span>
                  <span className={`db-task-completion-pct${guestStats.overCapacity ? ' db-guest-capacity--over' : ''}`}>
                    invited{guestStats.overCapacity ? ' — over capacity!' : ''}
                  </span>
                </div>
                <p className="db-card-summary db-card-summary-mt">{guestStats.confirmedGuests} confirmed</p>
              </>
            ) : (
              <>
                <p className="db-card-summary glance-strip">{guestStats.totalGuests} invited · {guestStats.confirmedGuests} confirmed</p>
                <p className="db-guest-capacity db-guest-capacity--unset">No guest capacity set — configure it in Settings.</p>
              </>
            )}
            {groupCounts.map(g => (
              <div key={g.tagId} className="db-gg-row">
                <span className="db-gg-label">{g.label}</span>
                <div className="db-gg-bar-wrap">
                  <div
                    className="db-gg-bar-fill"
                    style={{ /* ds-dynamic: fill percent + group color computed per row */ width: `${Math.round(g.count / maxGroupCount * 100)}%`, background: g.color }}
                  />
                </div>
                <span className="db-gg-count">{g.count}</span>
              </div>
            ))}
          </div>

          <div className="db-recent-activity">
            <div className="progress-header">
              <h2>Recent Activity</h2>
            </div>
            {recentActivity.length === 0 ? (
              <p className="db-recent-activity-empty">Nothing yet.</p>
            ) : (
              recentActivity.map(entry => (
                <div key={entry.id} className="db-recent-activity-row">
                  <span className={`db-recent-activity-action db-recent-activity-action--${entry.action}`}>
                    {ACTIVITY_ACTION_LABELS[entry.action] ?? entry.action}
                  </span>
                  <span className="db-recent-activity-entity">
                    {entry.summary
                      ? stripLeadingVerb(entry.summary, ACTIVITY_ACTION_LABELS[entry.action] ?? entry.action)
                      : describeEntity(entry)}
                  </span>
                  <span className="db-recent-activity-time">{formatActivityTime(entry.createdAt)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Budget Overview ── (shown when the budget API works; left out when it fails, since zeros would read as real numbers) */}
        {budgetAvailable && (
        <>
        <div className="section-header">
          <h2>Budget Overview</h2>
          <div className="db-budget-overview-header-right">
            <span className="db-budget-overview-summary">
              {formatCurrency(budgetSummary.totalActual)} of {formatCurrency(budgetSummary.totalPlanned)} spent
              <strong>
                {' '}({budgetSummary.totalPlanned > 0 ? Math.round(budgetSummary.totalActual / budgetSummary.totalPlanned * 100) : 0}%)
              </strong>
            </span>
            <button type="button" className="db-card-view-all" onClick={() => navigate('/budget')}>View all →</button>
          </div>
        </div>
        <p className="db-card-summary glance-strip">
          {budgetCategories.reduce((sum, cat) => sum + cat.vendors.length, 0)} vendors assigned across {budgetCategories.length} categor{budgetCategories.length === 1 ? 'y' : 'ies'}
        </p>
        <div className="budget-cards-grid">
          {topBudgetCategories.map(cat => (
            <div key={cat.id} className="budget-card">
              <div className="progress-item-header">
                <span className="progress-item-title">{cat.name}</span>
                <span className="progress-item-percentage">{getProgress(cat)}%</span>
              </div>
              <div className="progress-bar-container">
                <div className="progress-bar-fill" style={ { width: `${getProgress(cat)}%` }} />
              </div>
              <div className="progress-text">
                <span>{formatCurrency(cat.actual_amount)} spent</span>
                <span>{formatCurrency(cat.planned_amount)} planned</span>
              </div>
              <div className="budget-card-vendors">
                {cat.vendors.length === 0 ? (
                  <span className="budget-card-no-vendor">No vendor assigned</span>
                ) : (
                  cat.vendors.slice(0, 3).map(v => (
                    <div key={v.vendor_id} className="budget-card-vendor-row">
                      <div className="budget-card-vendor-dot" />
                      <span className="budget-card-vendor-name">{v.vendor_name}</span>
                      {v.service && (
                        <span className="budget-card-vendor-service">{v.service}</span>
                      )}
                    </div>
                  ))
                )}
                {cat.vendors.length > 3 && (
                  <span className="budget-card-more-vendors">+{cat.vendors.length - 3} more</span>
                )}
              </div>
            </div>
          ))}
        </div>
        </>
        )}

    </div>
  );
};

export default Dashboard;
